import { useCallback, useEffect, useRef } from 'react'
import React from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from './hooks'

const AUTO_LOGOUT_TIME = 60 * 60 * 1000
const LAST_ACTIVITY_KEY = 'lastActivity'
const INACTIVITY_SESSION_KEY = 'auth:inactivitySession'

const AutoLogout: React.FC = () => {
	const { isAuthenticated, logout } = useAuth()
	const location = useLocation()
	const timeoutRef = useRef<number | null>(null)
	const isUserBlockedPage = location.pathname.includes('/user-blocked')

	const performLogout = useCallback(() => {
		logout()
	}, [logout])

	const resetTimer = useCallback(() => {
		if (timeoutRef.current) clearTimeout(timeoutRef.current)

		const now = Date.now()
		localStorage.setItem(LAST_ACTIVITY_KEY, now.toString())

		timeoutRef.current = window.setTimeout(() => {
			const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY)
			const currentTime = Date.now()
			if (
				lastActivity &&
				currentTime - parseInt(lastActivity, 10) >= AUTO_LOGOUT_TIME &&
				isAuthenticated &&
				!isUserBlockedPage
			) {
				performLogout()
			}
		}, AUTO_LOGOUT_TIME)
	}, [isAuthenticated, isUserBlockedPage, performLogout])

	useEffect(() => {
		if (!isAuthenticated || isUserBlockedPage) return

		const events: (keyof WindowEventMap)[] = [
			'mousemove',
			'keydown',
			'click',
			'scroll',
			'touchstart',
		]

		const handleActivity = () => {
			const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY)
			const now = Date.now()

			if (
				!lastActivity ||
				now - parseInt(lastActivity, 10) < AUTO_LOGOUT_TIME
			) {
				resetTimer()
			}
		}

		let hasActiveSession = false
		try {
			hasActiveSession =
				sessionStorage.getItem(INACTIVITY_SESSION_KEY) === '1'
		} catch {
			// Ignore storage failures and treat as a fresh auth session.
		}

		if (!hasActiveSession) {
			try {
				sessionStorage.setItem(INACTIVITY_SESSION_KEY, '1')
			} catch {
				// Ignore storage failures.
			}
			resetTimer()
		} else {
			const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY)
			if (lastActivity) {
				const lastActivityTime = parseInt(lastActivity, 10)
				if (
					!isNaN(lastActivityTime) &&
					Date.now() - lastActivityTime > AUTO_LOGOUT_TIME
				) {
					performLogout()
					return
				}
			}
			resetTimer()
		}

		events.forEach((event) => window.addEventListener(event, handleActivity))

		return () => {
			events.forEach((event) =>
				window.removeEventListener(event, handleActivity),
			)
			if (timeoutRef.current) clearTimeout(timeoutRef.current)
		}
	}, [isAuthenticated, isUserBlockedPage, performLogout, resetTimer])

	return null
}

export default AutoLogout
